import { FC, useCallback, useEffect, useState } from 'react';
import { Button, ListItemButton, ListItemIcon, ListItemText, Typography, List, Backdrop, Paper, TextField, CircularProgress, InputAdornment, Popover, FormControl } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import { toast } from 'react-toastify';
import { createNewFolder, getFolders, updateFolderName } from '../../../services/notes.service';
import DeletePopover from './DeleteNotePopup';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';

dayjs.extend(relativeTime);
dayjs.extend(isToday);

interface FolderListTypes {
  userId: any;
  selectedFolderId: any | null;
  setSelectedFolderId: (id: string) => void;
}

export const formatDate = (date?: string | Date | null) => {
  const parsedDate = dayjs(date);
  if (parsedDate.isToday()) {
    return parsedDate.fromNow();
  }
  return parsedDate.format("D-MMM-YYYY, h:mm A");
};


const FolderList: FC<FolderListTypes> = ({ userId, selectedFolderId, setSelectedFolderId }) => {
  const [folderList, setFolderList] = useState<any>([]);
  const [isFetchingFolder, setIsFetchingFolder] = useState<boolean>(false);
  const [isCreateNewFolder, setIsCreateNewFolder] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editableIndex, setEditableIndex] = useState<number | null>(null);
  const [folderEditName, setFolderEditName] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [deletableData, setDeletableData] = useState<any>(null);
  const [cloneFolders, setCloneFolders] = useState<any>([]);
  const [searchText, setSearchText] = useState<any>('');

  const handlePopoverClose = () => {
    requestAnimationFrame(() => {
      setAnchorEl(null);
    });
  };

  const fetchFolders = async (fromCreate: boolean = false) => {
    setIsFetchingFolder(true);
    try {
      const folderData = await getFolders(userId);
      if (folderData?.statusCode === 200 && folderData?.data?.length > 0) {
        setFolderList([...folderData?.data]);
        setCloneFolders([...folderData?.data]);
        if (!fromCreate) setSelectedFolderId(folderData?.data[0]?.id)
      }
    } catch (e) {
      console.log(e, "error")
    } finally {
      setIsFetchingFolder(false);
    }
  }

  const handleUpdateFolderName = async () => {
    setIsLoading(true);
    try {
      const updatedData = await updateFolderName(selectedFolderId, { name: folderEditName, userId });
      const folderData = await getFolders(userId);
      if (updatedData?.statusCode === 200 && folderData?.statusCode === 200 && folderData?.data?.length) {
        if(searchText?.length > 0) {
          const lowerQuery = searchText?.toLowerCase();
  
          const filterData = folderData?.data?.filter((folder: any) =>
            folder.name.toLowerCase().includes(lowerQuery)
          );
          setFolderList(_.cloneDeep(filterData));
        } else {
          setFolderList(_.cloneDeep(folderData?.data));
        }
        setCloneFolders(_.cloneDeep(folderData?.data));
        toast.success(updatedData?.message);
        resetEditState();
        document.getElementById("folders-container")?.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      } else {
        toast.error(updatedData?.message)
      }

    } catch (e) {
      console.log(e, "error");
    }
    setIsLoading(false);
  }

  const handleNewFolderSave = async () => {
    setIsLoading(true);
    try {
      const payload = {
        userId,
        name: newFolderName
      }
      const data = await createNewFolder(payload);
      if (data?.statusCode === 200 && data?.status === "success") {
        toast.success(data?.message);
        setSelectedFolderId(data?.data?.id)
        fetchFolders(true);
        setIsCreateNewFolder(false);
        setSearchText('');
      } else {
        toast.error(data?.message)
      }
    } catch (e) {
      console.log(e, "error")
    } finally {
      setIsLoading(false);
    }
  }

  const handleFolderEdit = (isEdit: boolean, folder?: any, folderIndex?: number) => {
    if (!isEdit) return resetEditState();

    if (folder?.isDefaultFolder) {
      toast.error("Can't edit default folder name!");
      return resetEditState();
    }

    setEditableIndex(folderIndex ?? null);
    setFolderEditName(folder?.name ?? "");
  };

  const resetEditState = () => {
    setEditableIndex(null);
    setFolderEditName("");
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, folderData: any) => {
    event.stopPropagation();
    setAnchorEl(event?.currentTarget);
    setDeletableData(folderData);
  };

  const handleSearchfolder = useCallback(
      _.debounce((text: any) => {
        const lowerQuery = text?.toLowerCase();
  
        const filterData = cloneFolders?.filter((folder: any) =>
          folder.name.toLowerCase().includes(lowerQuery)
        );
  
        if (filterData?.length > 0) {
          setSelectedFolderId(filterData?.[0]?.id)
        } else {
          setSelectedFolderId('')
        }
        setFolderList(filterData)
      }, 500),
      [cloneFolders]
    );

  useEffect(() => {
    if (userId) {
      fetchFolders();
    }
  }, [userId])


  return (
    <>
      <Typography variant="h6">Folders</Typography>
      <FormControl className="search-input">
        <TextField
          size="small"
          variant="outlined"
          placeholder='Search folder...'
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target?.value)
            handleSearchfolder(e.target?.value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </FormControl>
      <List id="folders-container" className="list-Container">
        {folderList?.length > 0 ? (
          folderList.map((folder: any, index: number) => (
            editableIndex !== index ? (
              <ListItemButton
                selected={selectedFolderId === folder.id}
                key={folder.id}
                onClick={() => {
                  setSelectedFolderId(folder.id);
                }}
                onDoubleClick={() => { handleFolderEdit(true, folder, index) }}
                disabled={isLoading}
              >
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText
                  primary={folder.name}
                  secondary={formatDate(folder.updatedAt)}
                />
                {!folder?.isDefaultFolder && <div style={{ padding: "1rem 0" }} onClick={(e: any) => handleClick(e, folder)}>
                  <MoreHorizIcon />
                </div>}
              </ListItemButton>
            ) : (
              <TextField
                id="standard-basic"
                placeholder='Type folder name'
                variant="outlined"
                sx={{ mt: 2, width: "100%" }}
                value={folderEditName}
                disabled={isLoading}
                onChange={(e) => {
                  setFolderEditName(e?.target.value)
                }}
                onKeyDown={(key) => {
                  if (["NumpadEnter", "Enter"].includes(key.code)) {
                    handleUpdateFolderName();
                  }
                }}
                helperText="Press Enter to save your changes."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className='cursor-pointer' onClick={() => handleFolderEdit(false)} >
                      {isLoading && <CircularProgress size={20} color="inherit" />}
                      <CloseIcon />
                    </InputAdornment>
                  )
                }}
              />
            )
          ))
        ) : (
          <ListItemButton className='position-center'>
            {!isFetchingFolder && <ListItemText primary="No Folders found" />}
          </ListItemButton>
        )}
        {(isFetchingFolder || isLoading) && (
          <ListItemButton className='position-center' sx={{ display: "flex", flexDirection: "column", gap: 1 }} >
            <CircularProgress size={20} />
            <ListItemText className="fade-text" primary="Loading..." />
          </ListItemButton>)
        }
      </List>
      <footer>
        <Button
          variant="contained"
          onClick={() => {
            setNewFolderName("");
            setIsCreateNewFolder(true);
          }}
          disabled={isLoading}
        >
          Create folder
        </Button>
      </footer>
      <Backdrop
        open={isCreateNewFolder}
        sx={{ zIndex: 1200 }}
        className="notification-container"
      >
        {isCreateNewFolder && (
          <Paper
            className="new-folder-container"
            sx={{ width: "30%", padding: 2 }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              New Folder
            </Typography>
            <div className="form-container">
              <TextField
                fullWidth
                label="Name"
                id="fullWidth"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e?.target?.value)}
              />
            </div>
            <div className="form-footer">
              <Button
                variant="outlined"
                onClick={() => setIsCreateNewFolder(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!newFolderName || isLoading}
                onClick={handleNewFolderSave}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                Ok
                {isLoading && <CircularProgress size={20} color="inherit" />}
              </Button>
            </div>
          </Paper>
        )}
      </Backdrop>
      {anchorEl && (
        <Popover
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorEl={anchorEl}
          className="notification-more-popover"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <div className="list-options" onClick={() => setIsDeleteOpen(true)}>
            <DeleteIcon />
            <Typography>Delete</Typography>
          </div>
          <div className="list-options disabled" >
            <ShareIcon />
            <Typography>Share with</Typography>
          </div>
        </Popover>
      )}
      {isDeleteOpen && (
        <DeletePopover
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeletableData(null);
            handlePopoverClose()
            fetchFolders();
          }}
          entityType="folder"
          deletableData={deletableData}
        />
      )}
    </>
  )
}

export default FolderList